import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import {
  cancelStaffOrder,
  confirmMyTwoStep,
  confirmTwoStep,
  fetchAuditLog,
  fetchStaffOrderRunners,
  goodwillStaffRefund,
  reassignStaffOrder,
  type AssignableRunner,
  fetchCancellations,
  fetchPriceFreshness,
  fetchRefunds,
  fetchRunnersNow,
  fetchStaffAreas,
  pauseStaffArea,
  type AreaRow,
  decideAmbassadorWithdrawal,
  fetchStaffReferrals,
  fetchStaffReferrer,
  flagAmbassador,
  markAmbassadorPaid,
  markReferralRewardGiven,
  type StaffReferrerDetail,
  fetchShopperAccount,
  fetchSignups,
  fetchStaffOrder,
  fetchStaffOrders,
  myTwoStepOff,
  newRecoveryCodes,
  searchShopperAccounts,
  shopperExportLink,
  startMyTwoStep,
  startTwoStep,
  twoStepOff,
  type AuditEntryRow,
  type ShopperAccountView,
  type StaffOrderDetail,
  type StaffTwoStep,
} from '../lib/api';
import { AllocateJobs } from '../components/AllocateJobs';
import { VoucherForLevel } from '../components/StaffVoucher';
import { PersonTools } from '../components/Unlock';
import { money } from '../lib/money';

/**
 * The admin panel's newer parts (Section Q): orders and their timelines, Runners working now,
 * the reports (signups, cancellations, refunds, price freshness), and, for the owner alone,
 * Shoppers' accounts, their data and the audit log. Also two-step codes for every staff
 * sign-in. Descriptive words carry the class `extra` (ruling 47): a screen reader always reads
 * them; they show on the screen only when "Show words on the screen" is on.
 */

const FIELD = 'w-full min-h-control rounded-2xl border-2 border-paper bg-paper text-ink p-3';

function useLoad<T>(load: () => Promise<T>): {
  data: T | null;
  problem: string;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [problem, setProblem] = useState('');
  const loader = useRef(load);
  loader.current = load;
  const reload = useCallback(() => {
    loader
      .current()
      .then((result) => {
        setData(result);
        setProblem('');
      })
      .catch((failure: unknown) =>
        setProblem(failure instanceof Error ? failure.message : 'That could not be loaded.'),
      );
  }, []);
  useEffect(reload, [reload]);
  return { data, problem, reload };
}

function Failure({ text }: { text: string }): JSX.Element | null {
  if (text === '') return null;
  return (
    <p role="alert" className="border-2 border-paper bg-paper text-ink p-4 rounded-2xl m-0">
      {text}
    </p>
  );
}

function when(at: string): string {
  return new Date(at).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "14:05", UK time: when a Runner was last seen. */
function clock(at: string): string {
  return new Date(at).toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A date box's value, the start of that day, as the server wants it. */
function dayStart(value: string): string | undefined {
  return value === '' ? undefined : new Date(`${value}T00:00:00`).toISOString();
}

/** The day after a date box's value, so "to" includes the whole day. */
function dayEnd(value: string): string | undefined {
  if (value === '') return undefined;
  const day = new Date(`${value}T00:00:00`);
  day.setDate(day.getDate() + 1);
  return day.toISOString();
}

function fail(set: (text: string) => void): (failure: unknown) => void {
  return (failure) => set(failure instanceof Error ? failure.message : 'That did not work.');
}

/* ----------------------------------------------------------------------------- orders */

/**
 * The Orders tab: finding orders and their timelines, and, for the owner and staff he lets
 * allocate (ruling 80), "Allocate jobs", sending orders straight to chosen Runners.
 */
export function Orders({
  staffKey,
  by = 'Staff',
  onNews = () => undefined,
  canAllocate = false,
}: {
  staffKey: string;
  /** Whose name goes on a rescue. */
  by?: string;
  onNews?: (news: string) => void;
  /** May send orders straight to a chosen Runner (ruling 80). */
  canAllocate?: boolean;
}): JSX.Element {
  const [view, setView] = useState<'find' | 'allocate'>('find');
  if (!canAllocate) return <FindOrders staffKey={staffKey} by={by} onNews={onNews} />;
  return (
    <div className="space-y-4">
      <div role="group" aria-label="Orders views" className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={view === 'find'}
          onClick={() => setView('find')}
          className={`control ${view === 'find' ? 'bg-highlight text-on-accent' : 'border-paper bg-paper/10 text-paper'}`}
        >
          Find orders
        </button>
        <button
          type="button"
          aria-pressed={view === 'allocate'}
          onClick={() => setView('allocate')}
          className={`control ${view === 'allocate' ? 'bg-highlight text-on-accent' : 'border-paper bg-paper/10 text-paper'}`}
        >
          Allocate jobs
        </button>
      </div>
      {view === 'find' ? (
        <FindOrders staffKey={staffKey} by={by} onNews={onNews} />
      ) : (
        <AllocateJobs staffKey={staffKey} onNews={onNews} />
      )}
    </div>
  );
}

function FindOrders({
  staffKey,
  by,
  onNews,
}: {
  staffKey: string;
  by: string;
  onNews: (news: string) => void;
}): JSX.Element {
  const [filters, setFilters] = useState<{
    view: 'live' | 'past' | 'all';
    status: string;
    search: string;
    from: string;
    to: string;
  }>({ view: 'live', status: '', search: '', from: '', to: '' });
  const list = useLoad(() =>
    fetchStaffOrders(staffKey, {
      view: filters.view,
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.search ? { search: filters.search } : {}),
      ...(dayStart(filters.from) ? { from: dayStart(filters.from) } : {}),
      ...(dayEnd(filters.to) ? { to: dayEnd(filters.to) } : {}),
    }),
  );
  const { reload } = list;
  useEffect(reload, [filters, reload]);
  const [open, setOpen] = useState<StaffOrderDetail | null>(null);
  const [problem, setProblem] = useState('');

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const value = (name: string): string => String(data.get(name) ?? '').trim();
    const view = value('orders-view');
    setFilters({
      view: view === 'past' || view === 'all' ? view : 'live',
      status: value('orders-status'),
      search: value('orders-search'),
      from: value('orders-from'),
      to: value('orders-to'),
    });
  };

  return (
    <section aria-labelledby="orders-heading" className="space-y-4 max-w-3xl">
      <h2 id="orders-heading" className="text-lead font-bold">
        Orders
      </h2>
      <p className="m-0 extra">
        Live orders are still on their way; past orders have finished. Search by the reference, the
        Shopper&rsquo;s name, the Runner or the postcode district. Open one to hear everything that
        happened to it, in order.
      </p>
      <form onSubmit={submit} className="space-y-2" aria-label="Find orders">
        <label htmlFor="orders-view" className="block font-bold">
          Which orders
        </label>
        <select id="orders-view" name="orders-view" defaultValue="live" className={FIELD}>
          <option value="live">Live now</option>
          <option value="past">Past</option>
          <option value="all">All</option>
        </select>
        <label htmlFor="orders-status" className="block font-bold">
          Status
        </label>
        <select id="orders-status" name="orders-status" defaultValue="" className={FIELD}>
          <option value="">Any</option>
          {(list.data?.statuses ?? []).map((row) => (
            <option key={row.status} value={row.status}>
              {row.words}
            </option>
          ))}
        </select>
        <label htmlFor="orders-search" className="block font-bold">
          Search
        </label>
        <input id="orders-search" name="orders-search" type="search" className={FIELD} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="block font-bold">
            From
            <input name="orders-from" type="date" className={FIELD} />
          </label>
          <label className="block font-bold">
            To
            <input name="orders-to" type="date" className={FIELD} />
          </label>
        </div>
        <button type="submit" className="control bg-highlight text-on-accent">
          Show orders
        </button>
      </form>
      <Failure text={list.problem || problem} />
      {list.data && (
        <p role="status" className="m-0">
          {list.data.orders.length === 0
            ? 'No orders match.'
            : `${list.data.orders.length} ${list.data.orders.length === 1 ? 'order' : 'orders'}.`}
        </p>
      )}
      <ul className="list-none m-0 p-0 space-y-3">
        {list.data?.orders.map((row) => (
          <li key={row.id} className="border-2 border-paper rounded-2xl p-4 space-y-2">
            <h3 className="m-0 font-bold">
              {row.reference}: {row.statusWords}
            </h3>
            <p className="m-0">
              {row.shopperName ?? 'A Shopper'}
              {row.area ? `, ${row.area}` : ''}. {row.itemCount}{' '}
              {row.itemCount === 1 ? 'item' : 'items'},{' '}
              {money(row.finalTotalPence ?? row.totalEstimatePence)}
              {row.paidBy === 'bank' ? ' by bank transfer' : ''}.
              {row.runnerName ? ` Runner: ${row.runnerName}.` : ''} Made {when(row.createdAt)}.
            </p>
            {row.cancelReason && <p className="m-0">Why cancelled: {row.cancelReason}</p>}
            <button
              type="button"
              onClick={() =>
                void fetchStaffOrder(staffKey, row.id).then(setOpen).catch(fail(setProblem))
              }
              className="control border-paper bg-paper/10 text-paper"
            >
              Open the timeline<span className="visually-hidden"> for order {row.reference}</span>
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <section
          aria-labelledby="timeline-heading"
          className="border-2 border-highlight rounded-2xl p-4 space-y-3"
        >
          <h3 id="timeline-heading" className="m-0 font-bold">
            Order {open.order.reference}, what happened
          </h3>
          <p className="m-0">
            {open.order.items.map((item) => `${item.quantity} x ${item.name}`).join(', ')}.
          </p>
          <ol className="m-0 ps-6 space-y-1">
            {open.timeline.map((event, index) => (
              <li key={`${event.at}-${index}`}>
                <time dateTime={event.at}>{when(event.at)}</time>: {event.what}
              </li>
            ))}
          </ol>
          <RescueTools
            key={open.order.id}
            staffKey={staffKey}
            by={by}
            order={open.order}
            onDone={(news) => {
              onNews(news);
              setOpen(null);
              reload();
            }}
          />
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="control bg-paper/10 text-paper underline"
          >
            Close the timeline
          </button>
        </section>
      )}
    </section>
  );
}

/** Statuses an order can still be cancelled from: not delivered, not already over. */
const CANCELLABLE = new Set([
  'draft',
  'confirmed',
  'paid',
  'offered',
  'accepted',
  'shopping',
  'receipt_submitted',
  'delivering',
]);
/** Statuses an order can be given to another Runner from: before the shopping is bought. */
const REASSIGNABLE = new Set(['paid', 'offered', 'accepted', 'shopping']);

/**
 * The order rescue tools (16 October 2026 review): cancel it and send the money back, give a
 * goodwill refund of a stated amount, or give a stuck order to another Runner. Each asks for a
 * reason the Shopper will read, and each is written in the audit log by the server.
 */
function RescueTools({
  staffKey,
  by,
  order,
  onDone,
}: {
  staffKey: string;
  by: string;
  order: StaffOrderDetail['order'];
  onDone: (news: string) => void;
}): JSX.Element {
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);
  const [runners, setRunners] = useState<AssignableRunner[] | null>(null);
  const id = `rescue-${order.id}`;
  const run = (action: () => Promise<{ message: string }>): void => {
    setProblem('');
    setBusy(true);
    action()
      .then((result) => onDone(result.message))
      .catch((failure: unknown) => {
        setProblem(failure instanceof Error ? failure.message : 'That did not work.');
        setBusy(false);
      });
  };
  const words = (form: HTMLFormElement, name: string): string =>
    String(new FormData(form).get(name) ?? '').trim();
  const canCancel = CANCELLABLE.has(order.status);
  const canReassign = REASSIGNABLE.has(order.status);
  const canRefund = order.paidBy === 'card' && (order.refundablePence ?? 0) > 0;
  const loadRunners = (): void => {
    void fetchStaffOrderRunners(staffKey, order.id)
      .then((result) => setRunners(result.runners))
      .catch(fail(setProblem));
  };

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-3 border-t-2 border-paper/40 pt-3"
    >
      <h4 id={`${id}-heading`} className="m-0 font-bold">
        Put it right
      </h4>
      <p className="m-0 extra">
        Each of these is written in the audit log with your name, and the Shopper is told in plain
        words. The Runner, if there is one, is told to stop.
      </p>
      <Failure text={problem} />
      {canCancel && (
        <form
          aria-label={`Cancel order ${order.reference}`}
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            const reason = words(event.currentTarget, 'cancel-reason');
            if (reason === '') return setProblem('Please say why, in words the Shopper will read.');
            run(() => cancelStaffOrder(staffKey, order.id, { reason, by }));
          }}
        >
          <label htmlFor={`${id}-cancel-reason`} className="block font-bold">
            Cancel this order: why?
          </label>
          <p className="m-0 extra">
            {order.paidBy === 'card'
              ? 'The card payment goes back in full, by the payment it came from. No card number is ever seen.'
              : 'A bank transfer that has arrived is put on the till cases, to send back by hand. One that has not arrived is simply cancelled.'}
          </p>
          <input id={`${id}-cancel-reason`} name="cancel-reason" className={FIELD} />
          <button type="submit" disabled={busy} className="control bg-paper text-ink">
            Cancel the order and send the money back
          </button>
        </form>
      )}
      {canRefund && (
        <form
          aria-label={`Goodwill refund on order ${order.reference}`}
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            const pence = Math.round(Number(words(event.currentTarget, 'goodwill-amount')) * 100);
            const reason = words(event.currentTarget, 'goodwill-reason');
            if (!Number.isFinite(pence) || pence <= 0)
              return setProblem('Please give the refund in pounds.');
            if (reason === '') return setProblem('Please say why, in words the Shopper will read.');
            run(() => goodwillStaffRefund(staffKey, order.id, { amountPence: pence, reason, by }));
          }}
        >
          <label htmlFor={`${id}-goodwill-amount`} className="block font-bold">
            Goodwill refund, in pounds (up to {money(order.refundablePence ?? 0)})
          </label>
          <input
            id={`${id}-goodwill-amount`}
            name="goodwill-amount"
            inputMode="decimal"
            className={FIELD}
          />
          <label htmlFor={`${id}-goodwill-reason`} className="block font-bold">
            Why, in words the Shopper will read
          </label>
          <input id={`${id}-goodwill-reason`} name="goodwill-reason" className={FIELD} />
          <button type="submit" disabled={busy} className="control bg-paper text-ink">
            Send the goodwill refund
          </button>
        </form>
      )}
      {canReassign && (
        <form
          aria-label={`Give order ${order.reference} to another Runner`}
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            const chosen = words(event.currentTarget, 'reassign-runner');
            run(() =>
              reassignStaffOrder(staffKey, order.id, {
                ...(chosen ? { runnerId: chosen } : {}),
                by,
              }),
            );
          }}
        >
          <label htmlFor={`${id}-reassign-runner`} className="block font-bold">
            Give it to another Runner
          </label>
          <p className="m-0 extra">
            {order.runnerName ? `${order.runnerName} is released and told to stop. ` : ''}
            Choose a Runner, or leave it to the dispatcher to offer it again.
          </p>
          <select
            id={`${id}-reassign-runner`}
            name="reassign-runner"
            defaultValue=""
            onFocus={() => {
              if (runners === null) loadRunners();
            }}
            className={FIELD}
          >
            <option value="">Anyone the dispatcher finds</option>
            {(runners ?? []).map((runner) => (
              <option key={runner.id} value={runner.id}>
                {runner.name}
                {runner.onShift ? ', on shift' : ', off shift'}, by {runner.travel}
              </option>
            ))}
          </select>
          <button type="submit" disabled={busy} className="control bg-paper text-ink">
            Release the Runner and offer it again
          </button>
        </form>
      )}
      {!canCancel && !canRefund && !canReassign && (
        <p className="m-0">Nothing can be done to this order from here.</p>
      )}
    </section>
  );
}

/* ----------------------------------------------------------------------------- Runners */

export function RunnersNow({ staffKey }: { staffKey: string }): JSX.Element {
  const list = useLoad(() => fetchRunnersNow(staffKey));
  const data = list.data;
  return (
    <section aria-labelledby="runners-heading" className="space-y-4 max-w-3xl">
      <h2 id="runners-heading" className="text-lead font-bold">
        Runners
      </h2>
      <p className="m-0 extra">
        Who is working now, on shift or on a job, and what each Runner has earned for their
        deliveries.
      </p>
      <Failure text={list.problem} />
      {data && (
        <>
          <p className="m-0">
            {data.activeNow} active now: {data.onShift} on shift, {data.onAJob} on a job.
          </p>
          <table className="w-full border-collapse">
            <caption className="text-left font-bold py-2">Every Runner, active first</caption>
            <thead>
              <tr>
                {['Runner', 'Now', 'Jobs today', 'Today', 'This week', 'Altogether'].map((cell) => (
                  <th key={cell} scope="col" className="text-left border-b-2 border-paper p-2">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.runners.map((row) => (
                <tr key={row.id}>
                  <th scope="row" className="text-left font-normal border-b border-paper/40 p-2">
                    {row.name}
                  </th>
                  <td className="border-b border-paper/40 p-2">
                    {row.job ? `On order ${row.job.reference}` : row.onShift ? 'On shift' : 'Off'}
                    {/* Where a Runner on a job was last seen (Anthony, 30 October 2026). */}
                    {row.job && (
                      <>
                        {'. '}
                        {row.lastSeenAt ? `Last seen at ${clock(row.lastSeenAt)}` : 'Not seen yet'}
                        {row.mapsLink && (
                          <>
                            {', '}
                            <a href={row.mapsLink} target="_blank" rel="noopener noreferrer">
                              open in maps
                              <span className="visually-hidden">
                                {' '}
                                for {row.name} (opens in a new tab)
                              </span>
                            </a>
                          </>
                        )}
                      </>
                    )}
                  </td>
                  <td className="border-b border-paper/40 p-2">{row.jobsToday}</td>
                  <td className="border-b border-paper/40 p-2">{money(row.earnedTodayPence)}</td>
                  <td className="border-b border-paper/40 p-2">{money(row.earnedWeekPence)}</td>
                  <td className="border-b border-paper/40 p-2">{money(row.earnedAllTimePence)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

/* ----------------------------------------------------------------------------- areas */

/**
 * The Areas tab (Anthony, 30 October 2026): every UK area anybody is in, most active first, with
 * its Shoppers, Runners, shops, organisations, Shoppers warned there was no Runner, and orders,
 * whether it has a Runner (every area is open, 30 October 2026), and a button to pause it or
 * unpause it. Each area is a heading of its own, so a screen reader can move from one to the next.
 */
export function Areas({
  staffKey,
  by = 'Staff',
  onNews = () => undefined,
}: {
  staffKey: string;
  /** Whose name goes on a pause. */
  by?: string;
  onNews?: (news: string) => void;
}): JSX.Element {
  const list = useLoad(() => fetchStaffAreas(staffKey));
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const areas = list.data?.areas ?? null;

  function togglePause(area: AreaRow): void {
    if (busy !== '') return;
    setBusy(area.area);
    setProblem('');
    pauseStaffArea(staffKey, area.area, !area.paused, by)
      .then((result) => {
        onNews(result.message);
        list.reload();
      })
      .catch(fail(setProblem))
      .finally(() => setBusy(''));
  }

  return (
    <section aria-labelledby="areas-heading" className="space-y-4 max-w-3xl">
      <h2 id="areas-heading" className="text-lead font-bold">
        Areas
      </h2>
      {/* Every area is open (Anthony, 30 October 2026): the status only says whether it has a
          Runner. Nothing here stops anybody ordering. */}
      <p className="m-0 extra">
        Each UK postcode area, London as one. Every area is open, and nobody is ever refused for
        where they are. Jobs go to Runners in the same area, nearest first. Where there is no Runner
        yet, or an area is paused, Shoppers are warned gently as they order, and can still go ahead;
        they are texted once when a Runner joins.
      </p>
      <Failure text={list.problem} />
      <Failure text={problem} />
      {areas && areas.length === 0 && <p className="m-0">Nobody has signed up in any area yet.</p>}
      {areas && areas.length > 0 && (
        <ul className="list-none m-0 p-0 space-y-4">
          {areas.map((area) => (
            <li key={area.area} className="border-2 border-paper rounded-2xl p-4 space-y-2">
              <h3 className="text-lead font-bold m-0">
                {area.name}
                {area.name !== area.area && <span className="font-normal"> ({area.area})</span>}
              </h3>
              <p className="m-0 font-bold">
                {area.statusWords}
                {area.paused && area.pausedBy ? `, by ${area.pausedBy}` : ''}
              </p>
              <ul className="m-0 ps-6">
                <li>
                  {area.shoppers} {area.shoppers === 1 ? 'Shopper' : 'Shoppers'}
                </li>
                <li>
                  {area.runnersApproved} approved{' '}
                  {area.runnersApproved === 1 ? 'Runner' : 'Runners'}, {area.runnersWaiting} waiting
                  for approval
                </li>
                <li>
                  {area.shopsApproved} approved {area.shopsApproved === 1 ? 'shop' : 'shops'},{' '}
                  {area.shopsWaiting} waiting
                </li>
                <li>
                  {area.organisations} {area.organisations === 1 ? 'organisation' : 'organisations'}
                </li>
                <li>{area.waitingList} warned there was no Runner, to be texted when one joins</li>
                <li>
                  {area.ordersToday} {area.ordersToday === 1 ? 'order' : 'orders'} today,{' '}
                  {area.ordersThisWeek} this week
                </li>
              </ul>
              <button
                type="button"
                disabled={busy === area.area}
                onClick={() => togglePause(area)}
                className="control border-paper bg-paper/10 text-paper disabled:opacity-70"
              >
                {area.paused ? `Unpause ${area.name}` : `Pause ${area.name}`}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ----------------------------------------------------------------------------- referrals */

/**
 * The Referrals tab (Anthony, 30 October 2026), for the founder and the operations manager:
 *
 * - everybody who has referred anybody, the most referrals first, with a search: their kind and
 *   area, how many joined and how many are active, the share active, the reward level reached,
 *   and "Mark reward given", which keeps who gave it and when. Opening a person lists each
 *   person they referred, when they joined, and whether they are active and why;
 * - the Ozi Ambassadors: their businesses, this month's earnings, what is pending, available and
 *   paid, and any refunds taken off (ruled 30 October 2026); "Mark paid" pays only what is
 *   available; withdrawals waiting for approval, to approve or refuse; and a flag that makes
 *   every withdrawal of theirs wait for a person.
 */
export function Referrals({
  staffKey,
  by = 'Staff',
  onNews = () => undefined,
}: {
  staffKey: string;
  /** Whose name goes on a reward given or a payment. */
  by?: string;
  onNews?: (news: string) => void;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const [asked, setAsked] = useState('');
  const list = useLoad(() => fetchStaffReferrals(staffKey, asked));
  const { reload } = list;
  useEffect(reload, [asked, reload]);
  const [open, setOpen] = useState<{ kind: string; id: string } | null>(null);
  const [detail, setDetail] = useState<StaffReferrerDetail | null>(null);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const data = list.data;

  useEffect(() => {
    if (!open) {
      setDetail(null);
      return;
    }
    fetchStaffReferrer(staffKey, open.kind, open.id).then(setDetail).catch(fail(setProblem));
  }, [open, staffKey]);

  function act(key: string, run: () => Promise<{ message: string }>): void {
    if (busy !== '') return;
    setBusy(key);
    setProblem('');
    run()
      .then((result) => {
        onNews(result.message);
        reload();
      })
      .catch(fail(setProblem))
      .finally(() => setBusy(''));
  }

  function find(event: FormEvent): void {
    event.preventDefault();
    setOpen(null);
    setAsked(search.trim());
  }

  return (
    <section aria-labelledby="referrals-heading" className="space-y-6 max-w-3xl">
      <h2 id="referrals-heading" className="text-lead font-bold">
        Referrals
      </h2>
      {data && (
        <p className="m-0 extra">
          Reward levels:{' '}
          {data.levels
            .map((level) => `${level.activeReferrals} active referrals, ${level.label}`)
            .join('; ')}
          . A level counts only when at least {data.minimumActivePercent}% of everyone a person
          referred is active. Rewards are given by hand, as an {data.givenAs}. People never see who
          they referred, only their own counts.
        </p>
      )}
      <form onSubmit={find} className="space-y-2">
        <label htmlFor="referrals-search" className="block font-bold">
          Search by name, kind or area
        </label>
        <input
          id="referrals-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className={FIELD}
        />
        <button type="submit" className="control bg-highlight text-on-accent">
          Search
        </button>
      </form>
      <Failure text={list.problem} />
      <Failure text={problem} />

      <section aria-labelledby="referrers-heading" className="space-y-3">
        <h3 id="referrers-heading" className="text-lead font-bold m-0">
          Who has referred people
        </h3>
        {data && data.referrers.length === 0 && (
          <p className="m-0">
            {asked === '' ? 'Nobody has referred anybody yet.' : 'Nobody found.'}
          </p>
        )}
        {data && data.referrers.length > 0 && (
          <ul className="list-none m-0 p-0 space-y-4">
            {data.referrers.map((row) => {
              const opened = open?.kind === row.kind && open.id === row.id;
              return (
                <li
                  key={`${row.kind}:${row.id}`}
                  className="border-2 border-paper rounded-2xl p-4 space-y-2"
                >
                  <h4 className="text-lead font-bold m-0">
                    {row.name} <span className="font-normal">({row.kindWords})</span>
                  </h4>
                  <p className="m-0">
                    {row.area}. {row.joined} joined, {row.active} active ({row.percent}).{' '}
                    {row.level
                      ? `Reached the ${row.level.label} level: ${row.level.given ? 'reward given' : 'reward not given yet'}.`
                      : 'No reward level reached yet.'}
                  </p>
                  {/* Sending the voucher (30 October 2026): for every level reached. */}
                  {row.levels.map((level) => (
                    <VoucherForLevel
                      key={`voucher-${level.id}`}
                      staffKey={staffKey}
                      level={level}
                      personName={row.name}
                      by={by}
                      onSent={(message) => {
                        onNews(message);
                        reload();
                      }}
                    />
                  ))}
                  {row.levels
                    .filter((level) => level.givenAt === null)
                    .map((level) => (
                      <button
                        key={level.id}
                        type="button"
                        disabled={busy === level.id}
                        onClick={() =>
                          act(level.id, () => markReferralRewardGiven(staffKey, level.id, by))
                        }
                        aria-label={`Mark reward given: the ${level.label} for ${row.name}`}
                        className="control bg-highlight text-on-accent disabled:opacity-70"
                      >
                        Mark reward given
                      </button>
                    ))}
                  {row.levels
                    .filter((level) => level.givenAt !== null)
                    .map((level) => (
                      <p key={level.id} className="m-0">
                        The {level.label} was given by {level.givenBy ?? 'staff'},{' '}
                        {when(level.givenAt!)}.
                      </p>
                    ))}
                  <button
                    type="button"
                    aria-expanded={opened}
                    onClick={() => setOpen(opened ? null : { kind: row.kind, id: row.id })}
                    className="control border-paper bg-paper/10 text-paper"
                  >
                    {opened ? 'Hide' : 'Show'} the people {row.name} referred
                  </button>
                  {opened && detail && (
                    <ul className="m-0 ps-6 space-y-1">
                      {detail.people.map((person, index) => (
                        <li key={`${person.name}-${index}`}>
                          {person.name}, {person.kindWords}, joined {when(person.joinedAt)}:{' '}
                          {person.active ? 'active' : 'not active'}. {person.reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section aria-labelledby="ambassadors-heading" className="space-y-3">
        <h3 id="ambassadors-heading" className="text-lead font-bold m-0">
          Ozi Ambassadors
        </h3>
        <p className="m-0 extra">
          Earnings stay pending until the refund window after each delivery has passed. Only what is
          available can be paid, and a refund after that is taken off what becomes available next.
        </p>
        {data && data.ambassadors.length === 0 && (
          <p className="m-0">No Ambassador has introduced a business yet.</p>
        )}
        {data && data.ambassadors.length > 0 && (
          <ul className="list-none m-0 p-0 space-y-4">
            {data.ambassadors.map((row) => {
              const key = `${row.kind}:${row.id}`;
              return (
                <li key={key} className="border-2 border-paper rounded-2xl p-4 space-y-2">
                  <h4 className="text-lead font-bold m-0">
                    {row.name} <span className="font-normal">({row.kindWords})</span>
                  </h4>
                  {row.flagged && (
                    <p className="m-0 font-bold">
                      Flagged{row.flagReason ? `: ${row.flagReason}` : ''}. Every withdrawal waits
                      for a person.
                    </p>
                  )}
                  <ul className="m-0 ps-6">
                    {row.businesses.map((business) => (
                      <li key={business.id}>
                        {business.name}, {business.approved ? 'approved' : 'not approved yet'}
                        {business.branch ? ', another branch' : ''}: {money(business.earnedPence)}{' '}
                        earned
                      </li>
                    ))}
                  </ul>
                  <ul className="m-0 ps-6">
                    <li>This month: {money(row.earnedThisMonthPence)}</li>
                    <li>Pending: {money(row.pendingPence)}</li>
                    <li>Available: {money(row.availablePence)}</li>
                    <li>Paid: {money(row.paidPence)}</li>
                    <li>Refunds taken off: {money(row.deductionsPence)}</li>
                    {row.shortfallPence > 0 && (
                      <li>Still to take off their next earnings: {money(row.shortfallPence)}</li>
                    )}
                  </ul>
                  {row.deductions.map((line, index) => (
                    <p key={`${line.at}-${index}`} className="m-0">
                      {when(line.at)}: {line.words}
                    </p>
                  ))}
                  {row.waitingWithdrawals.map((withdrawal) => (
                    <div key={withdrawal.id} className="space-y-2">
                      <p className="m-0 font-bold">
                        Asked to withdraw {money(withdrawal.amountPence)},{' '}
                        {when(withdrawal.requestedAt)}. {withdrawal.waitingReason ?? ''}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={busy !== ''}
                          onClick={() =>
                            act(withdrawal.id, () =>
                              decideAmbassadorWithdrawal(staffKey, withdrawal.id, 'approve', by),
                            )
                          }
                          className="control bg-highlight text-on-accent disabled:opacity-70"
                        >
                          Approve and send
                          <span className="visually-hidden"> {row.name}&rsquo;s withdrawal</span>
                        </button>
                        <button
                          type="button"
                          disabled={busy !== ''}
                          onClick={() =>
                            act(withdrawal.id, () =>
                              decideAmbassadorWithdrawal(staffKey, withdrawal.id, 'refuse', by),
                            )
                          }
                          className="control border-paper bg-paper/10 text-paper disabled:opacity-70"
                        >
                          Refuse
                          <span className="visually-hidden"> {row.name}&rsquo;s withdrawal</span>
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-2">
                    {row.payable && (
                      <button
                        type="button"
                        disabled={busy !== ''}
                        onClick={() =>
                          act(key, () => markAmbassadorPaid(staffKey, row.kind, row.id, by))
                        }
                        className="control bg-highlight text-on-accent disabled:opacity-70"
                      >
                        Mark paid {money(row.availablePence)}
                        <span className="visually-hidden"> to {row.name}</span>
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busy !== ''}
                      onClick={() =>
                        act(`${key}:flag`, () =>
                          flagAmbassador(
                            staffKey,
                            row.kind,
                            row.id,
                            !row.flagged,
                            row.flagged ? '' : `Flagged by ${by}`,
                          ),
                        )
                      }
                      className="control border-paper bg-paper/10 text-paper disabled:opacity-70"
                    >
                      {row.flagged ? 'Take the flag off' : 'Flag'}
                      <span className="visually-hidden"> {row.name}</span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </section>
  );
}

/* ----------------------------------------------------------------------------- reports */

export function Reports({ staffKey }: { staffKey: string }): JSX.Element {
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('month');
  const signups = useLoad(() => fetchSignups(staffKey, period));
  const { reload } = signups;
  useEffect(reload, [period, reload]);
  const cancellations = useLoad(() => fetchCancellations(staffKey));
  const refunds = useLoad(() => fetchRefunds(staffKey));
  const prices = useLoad(() => fetchPriceFreshness(staffKey));

  return (
    <section aria-labelledby="reports-heading" className="space-y-8 max-w-3xl">
      <h2 id="reports-heading" className="text-lead font-bold">
        Reports
      </h2>

      <section aria-labelledby="signups-heading" className="space-y-3">
        <h3 id="signups-heading" className="font-bold m-0">
          Signups
        </h3>
        <label htmlFor="signups-period" className="block">
          By
        </label>
        <select
          id="signups-period"
          value={period}
          onChange={(event) =>
            setPeriod(
              event.target.value === 'day'
                ? 'day'
                : event.target.value === 'week'
                  ? 'week'
                  : 'month',
            )
          }
          className={FIELD}
        >
          <option value="day">Day</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
        </select>
        <Failure text={signups.problem} />
        {signups.data && (
          <table className="w-full border-collapse">
            <caption className="text-left py-2 extra">
              Shoppers and Runners who signed up, newest last
            </caption>
            <thead>
              <tr>
                {['When', 'Shoppers', 'Runners'].map((cell) => (
                  <th key={cell} scope="col" className="text-left border-b-2 border-paper p-2">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {signups.data.buckets.map((row) => (
                <tr key={row.from}>
                  <th scope="row" className="text-left font-normal border-b border-paper/40 p-2">
                    {row.label}
                  </th>
                  <td className="border-b border-paper/40 p-2">{row.shoppers}</td>
                  <td className="border-b border-paper/40 p-2">{row.runners}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section aria-labelledby="cancellations-heading" className="space-y-3">
        <h3 id="cancellations-heading" className="font-bold m-0">
          Cancellations, the last 30 days
        </h3>
        <Failure text={cancellations.problem} />
        {cancellations.data && (
          <>
            <p className="m-0">
              {cancellations.data.count === 0 ? 'None.' : `${cancellations.data.count} cancelled.`}
            </p>
            <ul className="m-0 ps-6">
              {cancellations.data.byReason.map((row) => (
                <li key={row.reason}>
                  {row.count}: {row.reason}
                </li>
              ))}
            </ul>
            <details>
              <summary className="control bg-paper/10 text-paper">Each cancellation</summary>
              <ul className="m-0 ps-6">
                {cancellations.data.cancellations.map((row) => (
                  <li key={row.id}>
                    {row.reference}, {when(row.at)}: {row.reason}
                  </li>
                ))}
              </ul>
            </details>
          </>
        )}
      </section>

      <section aria-labelledby="refunds-heading" className="space-y-3">
        <h3 id="refunds-heading" className="font-bold m-0">
          Refunds, the last 30 days
        </h3>
        <Failure text={refunds.problem} />
        {refunds.data && (
          <>
            <p className="m-0">
              {refunds.data.count === 0 ? 'None.' : `${refunds.data.count} went out`}
              {refunds.data.count > 0 && refunds.data.totalPence !== null
                ? `, ${money(refunds.data.totalPence)} altogether.`
                : refunds.data.count > 0
                  ? '.'
                  : ''}
            </p>
            <p className="m-0 extra">
              A refund of more than {money(refunds.data.ownerOnlyAbovePence)} is for the owner to
              give.
            </p>
            <ul className="m-0 ps-6 space-y-1">
              {refunds.data.refunds.map((row, index) => (
                <li key={`${row.at}-${index}`}>
                  {when(row.at)}, {money(row.amountPence)}: {row.reason}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section aria-labelledby="prices-heading" className="space-y-3">
        <h3 id="prices-heading" className="font-bold m-0">
          How fresh the prices are
        </h3>
        <Failure text={prices.problem} />
        {prices.data && (
          <>
            <p className="m-0">
              Of {prices.data.catalogue.total} prices: {prices.data.catalogue.fresh} seen in the
              last week, {prices.data.catalogue.ageing} in the last month, and{' '}
              {prices.data.catalogue.stale} longer ago.
            </p>
            {prices.data.catalogue.oldest.length > 0 && (
              <details>
                <summary className="control bg-paper/10 text-paper">The oldest prices</summary>
                <ul className="m-0 ps-6">
                  {prices.data.catalogue.oldest.map((row) => (
                    <li key={row.id}>
                      {row.name}, {money(row.pricePence)}, last seen{' '}
                      {new Date(row.lastSeenAt).toLocaleDateString('en-GB')}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {prices.data.shops.length > 0 && (
              <ul className="m-0 ps-6">
                {prices.data.shops.map((shop) => (
                  <li key={shop.id}>
                    {shop.name}:{' '}
                    {shop.lastUpdatedAt
                      ? `last updated ${new Date(shop.lastUpdatedAt).toLocaleDateString('en-GB')}`
                      : 'no products yet'}
                    {shop.stale ? ', not updated in the last 30 days' : ''}.
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </section>
  );
}

/* ----------------------------------------------------------------------------- the owner's alone */

export function ShopperAccounts({ staffKey }: { staffKey: string }): JSX.Element {
  const [results, setResults] = useState<Array<{
    id: string;
    displayName: string;
    handle: string;
    publicId?: string | null;
  }> | null>(null);
  const [open, setOpen] = useState<ShopperAccountView | null>(null);
  const [file, setFile] = useState<{ url: string; name: string } | null>(null);
  const [problem, setProblem] = useState('');

  const search = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const text = String(new FormData(event.currentTarget).get('shopper-search') ?? '').trim();
    setProblem('');
    searchShopperAccounts(staffKey, text)
      .then((result) => setResults(result.shoppers))
      .catch(fail(setProblem));
  };

  const account = open?.account;
  return (
    <section aria-labelledby="accounts-heading" className="space-y-4 max-w-3xl">
      <h2 id="accounts-heading" className="text-lead font-bold">
        Shopper accounts
      </h2>
      <p className="m-0">
        Only you can open a Shopper&rsquo;s account. Each one you open, and each export, is written
        in the audit log.
      </p>
      <form onSubmit={search} className="space-y-2" aria-label="Find a Shopper">
        <label htmlFor="shopper-search" className="block font-bold">
          Name, username or phone number
        </label>
        <input id="shopper-search" name="shopper-search" type="search" className={FIELD} />
        <button type="submit" className="control bg-highlight text-on-accent">
          Find
        </button>
      </form>
      <Failure text={problem} />
      {results && results.length === 0 && (
        <p role="status" className="m-0">
          Nobody matches.
        </p>
      )}
      <ul className="list-none m-0 p-0 space-y-2">
        {results?.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                void fetchShopperAccount(staffKey, row.id).then(setOpen).catch(fail(setProblem));
              }}
              className="control border-paper bg-paper/10 text-paper"
            >
              Open {row.displayName}
              {row.publicId ? ` (Shopper ID ${row.publicId})` : ''}
              <span className="visually-hidden">, {row.handle}</span>
            </button>
          </li>
        ))}
      </ul>
      {open && account && (
        <section
          aria-labelledby="account-heading"
          className="border-2 border-highlight rounded-2xl p-4 space-y-3"
        >
          <h3 id="account-heading" className="m-0 font-bold">
            {account.displayName}
          </h3>
          <dl className="m-0 space-y-1">
            {(
              [
                ['Username', account.handle],
                ['Phone', account.phone],
                ['Home address', account.deliveryAddress || 'Not given'],
                ['Joined', new Date(account.createdAt).toLocaleDateString('en-GB')],
              ] as Array<[string, string]>
            ).map(([label, value]) => (
              <div key={label}>
                <dt className="inline font-bold">{label}: </dt>
                <dd className="inline m-0">{value}</dd>
              </div>
            ))}
          </dl>
          <p className="m-0">
            Cards:{' '}
            {open.cards.length === 0
              ? 'none'
              : open.cards
                  .map((card) => `${card.brand ?? 'card'} ending ${card.lastFour}`)
                  .join(', ')}
            . Saved addresses: {open.savedAddresses.length}. Regular orders:{' '}
            {open.regularOrders.length}. Complaints: {open.problems.length}.
          </p>
          {/* Unlocking and resetting (ruling 81): each one audited, and the Shopper texted. */}
          <h4 className="m-0 font-bold">PIN and signing in</h4>
          <PersonTools
            staffKey={staffKey}
            person={{
              account: 'shopper',
              id: account.id,
              name: account.displayName,
              phoneEnding: '',
              hasPin: true,
              pinLocked: false,
              signInCodesLocked: false,
            }}
            owner
          />
          <h4 className="m-0 font-bold">Orders</h4>
          {open.orders.length === 0 ? (
            <p className="m-0">None yet.</p>
          ) : (
            <ul className="m-0 ps-6">
              {open.orders.map((order) => (
                <li key={order.id}>
                  {order.reference}, {when(order.createdAt)}, {order.status},{' '}
                  {money(order.finalTotalPence ?? order.totalEstimatePence)}: {order.items}
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={() =>
              void shopperExportLink(staffKey, account.id)
                .then((url) =>
                  setFile({
                    url,
                    name: `shopper-${account.handle}-${new Date().toISOString().slice(0, 10)}.csv`,
                  }),
                )
                .catch(fail(setProblem))
            }
            className="control bg-highlight text-on-accent"
          >
            Export everything we hold about them
          </button>
          <p className="m-0 extra">
            For a subject access request: a spreadsheet file of everything held about this Shopper.
          </p>
          {file && (
            <a
              href={file.url}
              download={file.name}
              className="control border-paper bg-paper/10 text-paper"
            >
              Save the file
            </a>
          )}
        </section>
      )}
    </section>
  );
}

export function AuditLog({ staffKey }: { staffKey: string }): JSX.Element {
  const [filters, setFilters] = useState({ search: '', from: '', to: '' });
  const list = useLoad(() =>
    fetchAuditLog(staffKey, {
      ...(filters.search ? { search: filters.search } : {}),
      ...(dayStart(filters.from) ? { from: dayStart(filters.from) } : {}),
      ...(dayEnd(filters.to) ? { to: dayEnd(filters.to) } : {}),
    }),
  );
  const { reload } = list;
  useEffect(reload, [filters, reload]);
  const entries: AuditEntryRow[] = list.data?.entries ?? [];

  return (
    <section aria-labelledby="audit-heading" className="space-y-4 max-w-3xl">
      <h2 id="audit-heading" className="text-lead font-bold">
        Audit log
      </h2>
      <p className="m-0 extra">
        Everything done in the admin panel: who did it, what, to what, when, and from which internet
        address. Only you see it, and nothing in it can be changed or removed.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          const value = (name: string): string => String(data.get(name) ?? '').trim();
          setFilters({
            search: value('audit-search'),
            from: value('audit-from'),
            to: value('audit-to'),
          });
        }}
        className="space-y-2"
        aria-label="Search the audit log"
      >
        <label htmlFor="audit-search" className="block font-bold">
          Search: a name, what was done, an order or an address
        </label>
        <input id="audit-search" name="audit-search" type="search" className={FIELD} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="block font-bold">
            From
            <input name="audit-from" type="date" className={FIELD} />
          </label>
          <label className="block font-bold">
            To
            <input name="audit-to" type="date" className={FIELD} />
          </label>
        </div>
        <button type="submit" className="control bg-highlight text-on-accent">
          Search
        </button>
      </form>
      <Failure text={list.problem} />
      {list.data && (
        <p role="status" className="m-0">
          {entries.length === 0
            ? 'Nothing matches.'
            : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}, newest first.`}
        </p>
      )}
      <ol className="list-none m-0 p-0 space-y-2">
        {entries.map((entry) => (
          <li key={entry.id} className="border-2 border-paper rounded-2xl p-3">
            <p className="m-0">
              <time dateTime={entry.at}>{when(entry.at)}</time>: {entry.actorName}, {entry.words}
              {entry.target ? ` (${entry.target})` : ''}.
            </p>
            <p className="m-0 extra">
              From {entry.ip || 'an unknown address'}. {entry.detail}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ----------------------------------------------------------------------------- two-step codes */

/**
 * Two-step codes (Section Q): set up with an authenticator app, recovery codes given once.
 * The owner switches them off with his passcode, everyone else with their password, and only
 * until their grace period ends.
 */
export function TwoStepCodes({
  staffKey,
  state,
  owner = false,
  level = 3,
  onNews,
  onSession,
  onChanged,
}: {
  staffKey: string;
  state: StaffTwoStep | null | undefined;
  owner?: boolean;
  /** The heading's level where it sits: 2 as a tab of its own, 3 inside another part. */
  level?: 2 | 3;
  onNews: (text: string) => void;
  /** A fresh session after setting them up, which opens the person's job. */
  onSession?: (token: string) => void;
  onChanged?: () => void;
}): JSX.Element {
  const [setUp, setSetUp] = useState<{ secret: string; otpauth: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [problem, setProblem] = useState('');
  const [on, setOn] = useState(state?.on ?? false);
  const pendingToken = useRef<string | null>(null);
  const due = state ? new Date(state.dueAt) : null;
  const canSwitchOff = due !== null && Date.now() < due.getTime();
  const values = (event: FormEvent<HTMLFormElement>): ((name: string) => string) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    return (name) => String(data.get(name) ?? '').trim();
  };

  const Heading = level === 2 ? 'h2' : 'h3';
  const Subheading = level === 2 ? 'h3' : 'h4';
  return (
    <section aria-labelledby="two-step-heading" className="space-y-3 max-w-2xl">
      <Heading id="two-step-heading" className="text-lead font-bold m-0">
        Two-step codes
      </Heading>
      <p className="m-0">
        {on
          ? `On. Signing in asks for the 6-digit code from your authenticator app.${state?.on && state.recoveryCodesLeft >= 0 ? ` ${state.recoveryCodesLeft} recovery ${state.recoveryCodesLeft === 1 ? 'code' : 'codes'} left.` : ''}`
          : due
            ? `Off. They are a must from ${due.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.`
            : 'Off.'}
      </p>
      <p className="m-0 extra">
        An authenticator app on your phone, such as Google Authenticator or Microsoft Authenticator,
        shows a new 6-digit code every 30 seconds. Nobody from us will ever ask you for one.
      </p>
      <Failure text={problem} />
      {codes && (
        <div className="border-2 border-highlight rounded-2xl p-4 space-y-2">
          <Subheading className="m-0 font-bold">Your recovery codes</Subheading>
          <p className="m-0">
            Write these down and keep them somewhere safe. Each one works once, in place of a code
            from the app, if you lose your phone. They are shown only now.
          </p>
          <ul className="m-0 ps-6 font-mono">
            {codes.map((code) => (
              <li key={code} aria-label={code.split('').join(' ')}>
                {code}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => {
              setCodes(null);
              if (pendingToken.current) onSession?.(pendingToken.current);
              pendingToken.current = null;
            }}
            className="control bg-highlight text-on-accent"
          >
            I have written them down
          </button>
        </div>
      )}
      {!on && !setUp && (
        <button
          type="button"
          onClick={() =>
            void (owner ? startTwoStep(staffKey) : startMyTwoStep(staffKey))
              .then((result) => {
                setProblem('');
                setSetUp(result);
              })
              .catch(fail(setProblem))
          }
          className="control bg-highlight text-on-accent"
        >
          Set up two-step codes
        </button>
      )}
      {!on && setUp && (
        <form
          onSubmit={(event) => {
            const value = values(event);
            (owner ? confirmTwoStep : confirmMyTwoStep)(staffKey, value('two-step-code'))
              .then((result) => {
                setProblem('');
                setSetUp(null);
                setOn(true);
                setCodes(result.recoveryCodes ?? null);
                pendingToken.current = result.token ?? null;
                if (!result.recoveryCodes && result.token) onSession?.(result.token);
                onNews(result.message);
                onChanged?.();
              })
              .catch(fail(setProblem));
          }}
          className="space-y-2"
          aria-label="Switch two-step codes on"
        >
          <p className="m-0 break-all">
            Add this key to your authenticator app:{' '}
            <span aria-label={setUp.secret.split('').join(' ')}>{setUp.secret}</span>
          </p>
          <a href={setUp.otpauth} className="control border-paper bg-paper/10 text-paper">
            Open it in my authenticator app
          </a>
          <label htmlFor="two-step-code" className="block">
            The 6-digit code it shows
          </label>
          <input
            id="two-step-code"
            name="two-step-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            className={FIELD}
          />
          <button type="submit" className="control bg-highlight text-on-accent">
            Switch two-step codes on
          </button>
        </form>
      )}
      {on && !codes && (
        <form
          onSubmit={(event) => {
            const value = values(event);
            newRecoveryCodes(staffKey, value('recovery-code'))
              .then((result) => {
                setProblem('');
                setCodes(result.recoveryCodes);
                onNews(result.message);
              })
              .catch(fail(setProblem));
          }}
          className="space-y-2"
          aria-label="New recovery codes"
        >
          <label htmlFor="recovery-code" className="block">
            A code from your app, for new recovery codes
          </label>
          <input
            id="recovery-code"
            name="recovery-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            className={FIELD}
          />
          <button type="submit" className="control border-paper bg-paper/10 text-paper">
            Make new recovery codes
          </button>
        </form>
      )}
      {on && canSwitchOff && !codes && (
        <form
          onSubmit={(event) => {
            const value = values(event);
            (owner
              ? twoStepOff(staffKey, value('two-step-off'))
              : myTwoStepOff(staffKey, value('two-step-off'))
            )
              .then((result) => {
                setProblem('');
                setOn(false);
                onNews(result.message);
                onChanged?.();
              })
              .catch(fail(setProblem));
          }}
          className="space-y-2"
          aria-label="Switch two-step codes off"
        >
          <label htmlFor="two-step-off" className="block">
            {owner ? 'Your passcode, to switch them off' : 'Your password, to switch them off'}
          </label>
          <input
            id="two-step-off"
            name="two-step-off"
            type="password"
            autoComplete={owner ? 'off' : 'current-password'}
            className={FIELD}
          />
          <button type="submit" className="control bg-paper/10 text-paper underline">
            Switch two-step codes off
          </button>
        </form>
      )}
    </section>
  );
}

/** Shown in place of everything else once the grace period is over and they are not set up. */
export function TwoStepRequired({
  staffKey,
  state,
  owner,
  onNews,
  onSession,
}: {
  staffKey: string;
  state: StaffTwoStep;
  owner: boolean;
  onNews: (text: string) => void;
  onSession: (token: string) => void;
}): JSX.Element {
  return (
    <section aria-labelledby="two-step-needed-heading" className="space-y-4 max-w-2xl">
      <h2 id="two-step-needed-heading" className="text-lead font-bold m-0">
        Please set up two-step codes
      </h2>
      <p className="m-0">
        Every admin sign-in now needs a code from an authenticator app as well as your password.
        Nothing else opens until it is set up. It takes about two minutes.
      </p>
      <TwoStepCodes
        staffKey={staffKey}
        state={state}
        owner={owner}
        onNews={onNews}
        onSession={onSession}
      />
    </section>
  );
}
